/**
 * Alerts data hook: fetches the encrypted feed from the control plane and
 * decrypts individual rows on demand (decrypt-on-display).
 *
 * Each decryption call runs scrypt at work factor 18 which takes ~1s on a
 * warm laptop. We therefore:
 *   1. Fetch the full feed as opaque ciphertext (fast).
 *   2. Let the route drive `decryptAlert(id)` as cards scroll into view.
 *   3. Cache the plaintext in `decryptCache` keyed by alert id, so scrolling
 *      away and back doesn't re-pay the scrypt cost.
 *
 * The AAD binds the alert id at encrypt time (see `e2ee_ops::encrypt_alert_body`
 * on the Rust side). We must pass the same 16 raw bytes here — the canonical
 * hyphenated string form does *not* match, hence `uuidToBytes`.
 *
 * Filter + pagination state (2026-07 rework):
 *   - `dateFrom` / `dateTo` are yyyy-mm-dd strings from `<input type="date">`.
 *     Empty string means "no bound". They're converted to ISO8601 before the
 *     server sees them.
 *   - `endpointFilter` is a list of endpoint UUIDs. Empty list = all.
 *   - `loadOlder()` walks backwards using the `received_at` of the oldest
 *     row currently on screen as the cursor; polling is gone in favour of
 *     an explicit Refresh button.
 */

import { signal } from '@preact/signals';
import { api, apiDelete } from '../state/api';
import { decrypt } from '../e2ee/envelope';
import { familyKeyInMemory } from '../state/session';
import { uuidToBytes } from './uuid';

/**
 * Decrypted alert payload. Shape matches what the endpoint scheduler writes
 * in `crates/backstop-core/src/scheduler.rs::build_alert`. Prose-taxonomy
 * rewrite (2026-07-05) replaced `description + severity` with `reason` +
 * `category_label` + `screen_summary`.
 */
export interface AlertBody {
  /** Category id from the taxonomy, e.g. `"test-kittens"`. */
  category_id: string;
  /** Human-readable category label for the card header. */
  category_label: string;
  /** One-sentence reason the LLM produced for the flag. */
  reason: string;
  /** One-sentence summary of what the endpoint saw on screen at capture. */
  screen_summary: string;
  /** RFC3339 timestamp of the capture that produced this alert. */
  captured_at: string;
}

/** Triage classification landed with migration 0020. `unreviewed` is
 *  the default state; the feed defaults to hiding everything else so the
 *  pending list stays actionable. */
export type TriageState =
  'unreviewed' | 'reviewed' | 'false_positive' | 'discussed';

export interface AlertRow {
  id: string;
  endpoint_id: string;
  timestamp: string;
  severity_bucket: number;
  content_ct: number[];
  received_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  triage_state: TriageState;
  /** When the parent archived this alert; `null` while it is in the inbox. */
  archived_at: string | null;
  decrypted?: AlertBody;
  decryptFailed?: boolean;
}

/** Envelope returned by `GET /v1/family/alerts`. */
interface FeedResponse {
  alerts: AlertRow[];
  /** Rows matching the current filters, ignoring limit/offset. */
  total: number;
  /** Tab counts — deliberately unfiltered, so a device filter narrows the
   *  list without making the Archive tab claim the archive shrank. */
  inbox_total: number;
  archived_total: number;
  retention_days: number;
}

/** Rows per page. The server clamps `limit` to `[1, 200]`. */
export const PAGE_SIZE = 25;

export const alerts = signal<AlertRow[]>([]);
export const loading = signal(false);
export const decryptCache = new Map<string, AlertBody>();

/** Which tab is showing. `false` = inbox, `true` = archive. */
export const showArchived = signal(false);
/** Zero-based page index within the current tab. */
export const page = signal(0);
/** Rows matching the current filters, for "1–25 of N". */
export const total = signal(0);
/** Unfiltered tab counts. */
export const inboxTotal = signal(0);
export const archivedTotal = signal(0);
/**
 * Days an archived alert survives. Server-supplied rather than hardcoded so
 * the countdown a parent reads can never drift from the window the reaper
 * actually enforces.
 */
export const retentionDays = signal(30);

/** Filter state — surfaced to the route as signals so the header bar can
 *  bind directly and effects can react. `null` / empty = "no bound". */
export const dateFrom = signal<string | null>(null);
export const dateTo = signal<string | null>(null);
export const endpointFilter = signal<string[]>([]);

/** Ids currently ticked in selection mode. */
export const selection = signal<Set<string>>(new Set());

/** Total pages in the current tab, minimum 1 so the control never vanishes. */
export function pageCount(): number {
  return Math.max(1, Math.ceil(total.value / PAGE_SIZE));
}

/**
 * Whole days left before the reaper deletes an archived alert.
 *
 * Clamped at zero rather than going negative: the reaper runs hourly, so a
 * row can legitimately be a few minutes past its window and still be on
 * screen. "Deletes in -1 days" would read as a bug.
 */
export function daysUntilPurge(row: AlertRow): number | null {
  if (row.archived_at === null) return null;
  const due =
    new Date(row.archived_at).getTime() +
    retentionDays.value * 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((due - Date.now()) / (24 * 60 * 60 * 1000)));
}

/** Convert a yyyy-mm-dd form-input string into an ISO8601 timestamp anchored
 *  to UTC midnight (for `from`) or the end of the day (for `to`), so a user
 *  who picks a single day sees the whole day. */
function isoFromDateInput(
  value: string | null,
  edge: 'start' | 'end',
): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return edge === 'start'
    ? `${trimmed}T00:00:00.000Z`
    : `${trimmed}T23:59:59.999Z`;
}

function buildQuery(): string {
  const search = new URLSearchParams();
  search.set('limit', String(PAGE_SIZE));
  search.set('offset', String(page.value * PAGE_SIZE));
  search.set('archived', String(showArchived.value));
  const from = isoFromDateInput(dateFrom.value, 'start');
  if (from !== null) search.set('from', from);
  const to = isoFromDateInput(dateTo.value, 'end');
  if (to !== null) search.set('to', to);
  const ids = endpointFilter.value;
  if (ids.length > 0) search.set('endpoint_ids', ids.join(','));
  return `?${search.toString()}`;
}

/** Whether the family has ever received an alert, inbox or archive. Does
 *  not touch the feed signals. */
export async function fetchHasAnyAlert(): Promise<boolean> {
  const resp = await api<FeedResponse>('/v1/family/alerts?limit=1');
  return resp.inbox_total + resp.archived_total > 0;
}

/**
 * Fetch the current page of the current tab, replacing the in-memory list.
 * Called on mount, on Refresh, and whenever a filter, tab, or page signal
 * changes.
 */
export async function refresh(): Promise<void> {
  loading.value = true;
  try {
    const resp = await api<FeedResponse>(`/v1/family/alerts${buildQuery()}`);
    alerts.value = resp.alerts.map(hydrateFromCache);
    total.value = resp.total;
    inboxTotal.value = resp.inbox_total;
    archivedTotal.value = resp.archived_total;
    retentionDays.value = resp.retention_days;

    // A sweep or a purge can empty the page the parent is standing on.
    // Walking back rather than showing an empty list keeps the control and
    // the content agreeing with each other.
    if (resp.alerts.length === 0 && page.value > 0 && resp.total > 0) {
      page.value = Math.min(page.value, pageCount() - 1);
      await refresh();
      return;
    }
    // Selection is per-view; carrying ticks across a tab or page change is
    // how someone deletes a row they cannot see.
    selection.value = new Set();
  } finally {
    loading.value = false;
  }
}

/** Switch tab, resetting pagination and selection. */
export function setTab(archived: boolean): void {
  if (showArchived.value === archived) return;
  showArchived.value = archived;
  page.value = 0;
}

/** Move to a page, clamped into range. */
export function setPage(next: number): void {
  page.value = Math.max(0, Math.min(next, pageCount() - 1));
}

/** Stamp a row's `decrypted` field from `decryptCache` if we already have
 *  the plaintext. Every replacement of `alerts.value` from server data
 *  must run through this, otherwise cache-hit paths in `decryptAlert`
 *  return the body without updating the signal and cards stay stuck in
 *  the "DECRYPTING" state forever. */
function hydrateFromCache(row: AlertRow): AlertRow {
  const cached = decryptCache.get(row.id);
  return cached === undefined ? row : { ...row, decrypted: cached };
}

/** Named server-side sweeps. See `bulk_archive` / `purge_alerts`. */
export type SweepScope = 'triaged' | 'older_than_30d' | 'all';

/**
 * Archive one alert. Reversible, so no confirmation — the caller shows an
 * undo toast instead.
 *
 * Removes the row from the local list immediately when we are looking at
 * the inbox. Optimism is safe here: `refresh()` follows, and the worst case
 * is a row reappearing rather than one vanishing wrongly.
 */
export async function archive(id: string): Promise<void> {
  await api(`/v1/family/alerts/${id}/archive`, { method: 'POST' });
  alerts.value = alerts.value.filter((a) => a.id !== id);
  inboxTotal.value = Math.max(0, inboxTotal.value - 1);
  archivedTotal.value += 1;
}

/** Put an archived alert back in the inbox. Backs both Undo and Restore. */
export async function restore(id: string): Promise<void> {
  await api(`/v1/family/alerts/${id}/restore`, { method: 'POST' });
  alerts.value = alerts.value.filter((a) => a.id !== id);
  archivedTotal.value = Math.max(0, archivedTotal.value - 1);
  inboxTotal.value += 1;
}

/**
 * Permanently delete one archived alert.
 *
 * No confirmation by design: the parent archived it and then navigated to
 * the archive tab, which is already two deliberate acts. The server refuses
 * (409) if the alert is somehow still in the inbox, so the invariant does
 * not depend on this client getting it right.
 */
export async function remove(id: string): Promise<void> {
  await apiDelete(`/v1/family/alerts/${id}`);
  alerts.value = alerts.value.filter((a) => a.id !== id);
  archivedTotal.value = Math.max(0, archivedTotal.value - 1);
  decryptCache.delete(id);
}

/** Archive a set of ids, or run a named sweep server-side. */
export async function archiveBulk(
  arg: { ids: string[] } | { scope: SweepScope },
): Promise<number> {
  const body = await api<{ archived: number }>('/v1/family/alerts/archive', {
    method: 'POST',
    body: JSON.stringify(arg),
  });
  return body.archived;
}

/** Restore a set of ids, or the whole archive with `{ scope: 'all' }`. */
export async function restoreBulk(
  arg: { ids: string[] } | { scope: SweepScope },
): Promise<number> {
  const body = await api<{ restored: number }>('/v1/family/alerts/restore', {
    method: 'POST',
    body: JSON.stringify(arg),
  });
  return body.restored;
}

/**
 * Permanently delete archived alerts — a selection, or the whole archive.
 *
 * This is the one destructive action that keeps a confirmation dialog, and
 * the caller is responsible for showing it: plural and irreversible is a
 * different weight of action from a single row.
 */
export async function purge(
  arg: { ids: string[] } | { scope: 'all' },
): Promise<number> {
  const body = await api<{ deleted: number }>('/v1/family/alerts/purge', {
    method: 'POST',
    body: JSON.stringify(arg),
  });
  for (const id of 'ids' in arg ? arg.ids : []) decryptCache.delete(id);
  return body.deleted;
}

/** Toggle one row's selection. */
export function toggleSelected(id: string): void {
  const next = new Set(selection.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  selection.value = next;
}

/** Select every row on the current page, or clear if all are already on. */
export function toggleSelectAll(): void {
  const ids = alerts.value.map((a) => a.id);
  const allOn = ids.length > 0 && ids.every((id) => selection.value.has(id));
  selection.value = allOn ? new Set() : new Set(ids);
}

/**
 * Decrypt (or return from cache) the plaintext body for the alert with the
 * given id. Mutates the in-signal row so downstream renders show the
 * decrypted content.
 *
 * Returns null without touching the row while the family key is not yet in
 * memory (it hydrates asynchronously at boot); callers re-run once
 * `familyKeyInMemory` is populated.
 */
export async function decryptAlert(id: string): Promise<AlertBody | null> {
  const cached = decryptCache.get(id);
  if (cached !== undefined) return cached;

  const fk = familyKeyInMemory.value;
  if (fk === null) return null;
  const row = alerts.value.find((a) => a.id === id);
  if (row === undefined) return null;

  try {
    const ct = new Uint8Array(row.content_ct);
    const aad = uuidToBytes(row.id);
    const pt = await decrypt(fk, ct, aad);
    const body = JSON.parse(new TextDecoder().decode(pt)) as AlertBody;
    decryptCache.set(id, body);
    alerts.value = alerts.value.map((a) =>
      a.id === id ? { ...a, decrypted: body } : a,
    );
    return body;
  } catch {
    alerts.value = alerts.value.map((a) =>
      a.id === id ? { ...a, decryptFailed: true } : a,
    );
    return null;
  }
}

/**
 * Move an alert into a triage state (see migration 0020). Triage
 * classifies; it does not remove. The inbox collapses a triaged row to a
 * single line so the day's shape stays honest, and archiving is the
 * separate, deliberate second step that clears it.
 *
 * Updates local state optimistically. Reverts on error so a failed
 * network call leaves the row visible + still pending.
 */
export async function triage(id: string, state: TriageState): Promise<void> {
  const before = alerts.value;
  alerts.value = alerts.value.map((a) =>
    a.id === id ? { ...a, triage_state: state } : a,
  );
  try {
    await api(`/v1/family/alerts/${id}/triage`, {
      method: 'POST',
      body: JSON.stringify({ state }),
    });
  } catch (e) {
    alerts.value = before;
    throw e;
  }
}

/** Clear all filter state back to defaults. Callers typically follow with
 *  `refresh()` — the route's filter-change effect will pick this up on its
 *  own if the reset happens inside a render. */
export function resetFilters(): void {
  dateFrom.value = null;
  dateTo.value = null;
  endpointFilter.value = [];
  page.value = 0;
}
